/**
 * Cognito session refresh for the admin app.
 * Amplify v6 persists access, ID, and refresh tokens and uses REFRESH_TOKEN_AUTH
 * internally when fetchAuthSession({ forceRefresh: true }) runs.
 */

import { fetchAuthSession, signOut } from 'aws-amplify/auth';

/** Refresh access/ID tokens when they expire within this window (ms). */
const PROACTIVE_REFRESH_BUFFER_MS = 5 * 60 * 1000;

let forcedRefreshInFlight = null;
let onRefreshFailure = null;

/**
 * Register cleanup when refresh fails (e.g. NotAuthorizedException).
 * Typically: clear Zustand + Amplify signOut so Authenticator shows login again.
 * @param {(() => void | Promise<void>) | null} handler
 */
export function setAuthRefreshFailureHandler(handler) {
  onRefreshFailure = handler;
}

/**
 * @param {unknown} error
 * @returns {boolean}
 */
export function isRefreshFailureError(error) {
  if (!error || typeof error !== 'object') return false;
  const name = /** @type {{ name?: string }} */ (error).name;
  const msg = String(/** @type {{ message?: string }} */ (error).message || '');
  const underlying = /** @type {{ underlyingError?: { name?: string } }} */ (error).underlyingError;
  return (
    name === 'NotAuthorizedException'
    || name === 'UserUnAuthenticatedException'
    || underlying?.name === 'NotAuthorizedException'
    || msg.includes('NotAuthorizedException')
  );
}

/**
 * @param {import('aws-amplify/auth').AuthTokens | null | undefined} tokens
 * @returns {number | null} expiry time in ms since epoch
 */
function getSessionExpiryMs(tokens) {
  const accessExp = tokens?.accessToken?.payload?.exp;
  const idExp = tokens?.idToken?.payload?.exp;
  const expSec = typeof accessExp === 'number' ? accessExp : idExp;
  if (typeof expSec !== 'number') return null;
  return expSec * 1000;
}

/**
 * @param {import('aws-amplify/auth').AuthTokens | null | undefined} tokens
 * @returns {boolean}
 */
function needsProactiveRefresh(tokens) {
  const expMs = getSessionExpiryMs(tokens);
  if (expMs == null) return false;
  return expMs - Date.now() <= PROACTIVE_REFRESH_BUFFER_MS;
}

function runSharedForcedRefresh() {
  if (!forcedRefreshInFlight) {
    forcedRefreshInFlight = (async () => {
      try {
        return await fetchAuthSession({ forceRefresh: true });
      } finally {
        forcedRefreshInFlight = null;
      }
    })();
  }
  return forcedRefreshInFlight;
}

/**
 * Clear server-side session state and invoke the registered UI handler.
 * @param {unknown} [error]
 */
export async function handleRefreshFailure(error) {
  if (process.env.NODE_ENV === 'development' && error) {
    console.warn('[auth] Refresh failed; signing out.', error);
  }
  if (onRefreshFailure) {
    try {
      await onRefreshFailure(error);
      return;
    } catch (e) {
      console.error('[auth] Refresh failure handler error:', e);
    }
  }
  try {
    await signOut();
  } catch (e) {
    console.error('[auth] signOut after refresh failure:', e);
  }
}

/**
 * Returns a session with valid tokens: uses Amplify’s built-in refresh for expired
 * tokens, and force-refreshes once (shared in-flight) when expiry is within the buffer.
 * @returns {Promise<import('aws-amplify/auth').AuthSession>}
 */
export async function ensureSessionWithRefresh() {
  let session;
  try {
    session = await fetchAuthSession();
  } catch (e) {
    if (isRefreshFailureError(e)) {
      await handleRefreshFailure(e);
    }
    throw e;
  }

  const tokens = session.tokens;
  if (!tokens?.idToken) {
    return session;
  }

  if (needsProactiveRefresh(tokens)) {
    try {
      session = await runSharedForcedRefresh();
    } catch (e) {
      if (isRefreshFailureError(e)) {
        await handleRefreshFailure(e);
      }
      throw e;
    }
  }

  return session;
}

/**
 * @returns {Promise<{ Authorization: string }>}
 */
export async function getBearerAuthHeaders() {
  const session = await ensureSessionWithRefresh();
  const id = session.tokens?.idToken?.toString();
  if (!id) {
    throw new Error('Authentication failed. Please sign in again.');
  }
  return { Authorization: `Bearer ${id}` };
}
